import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DxFormComponent, DxFormModule, DxFormTypes } from 'devextreme-angular/ui/form';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { FormPopupComponent } from 'src/app/components/utils/form-popup/form-popup.component';
import {
    BLOCK_DESCRIPTION_MAX_LENGTH,
    BlockableEntityType,
    IEntityBlockFormData,
    IEntityBlockRequest,
    SELECTABLE_BLOCK_REASON_TYPES,
} from 'src/app/features/entity-blocks/models/entity-block.models';
import { ButtonSection, IFooterButton } from 'src/app/types/footerButtons';

@Component({
    selector: 'app-entity-block-dialog',
    standalone: true,
    imports: [CommonModule, TranslocoPipe, FormPopupComponent, DxFormModule, DxSelectBoxModule, DxTextBoxModule, DxTextAreaModule],
    templateUrl: './entity-block-dialog.component.html',
    styleUrl: './entity-block-dialog.component.scss',
})
export class EntityBlockDialogComponent {
    private readonly translocoService = inject(TranslocoService);

    @ViewChild(DxFormComponent) form?: DxFormComponent;

    readonly descriptionMaxLength = BLOCK_DESCRIPTION_MAX_LENGTH;
    readonly reasonValidationRules = [
        {
            type: 'required',
            message: this.translocoService.translate('validation_required', {
                field: this.translocoService.translate('label_blockReason'),
            }),
        },
    ];

    visible = input(false);
    entityType = input<BlockableEntityType>(BlockableEntityType.Customer);
    entityName = input<string>('');
    entityId = input<string | null>(null);
    isSubmitting = input(false);

    visibleChange = output<boolean>();
    blockConfirmed = output<IEntityBlockRequest>();

    readonly formData = signal<IEntityBlockFormData>({
        entityType: this.entityType(),
        entityName: this.entityName(),
        reason: null,
        description: '',
    });

    readonly reasonOptions = computed(() =>
        SELECTABLE_BLOCK_REASON_TYPES.map(reason => ({
            value: reason,
            text: this.translocoService.translate(`enum_block_reason_${reason}`),
        }))
    );

    readonly entityTypeLabel = computed(() => this.translocoService.translate(`enum_blockable_entity_type_${this.entityType()}`));

    readonly isValid = signal(false);

    readonly footerButtons = computed<IFooterButton[]>(() => [
        {
            text: 'cancel',
            action: 'close',
            section: ButtonSection.Right,
            type: 'normal',
            stylingMode: 'outlined',
            disabled: this.isSubmitting(),
        },
        {
            text: 'block',
            action: 'block',
            section: ButtonSection.Right,
            type: 'default',
            stylingMode: 'contained',
            disabled: !this.isValid() || this.isSubmitting(),
        },
    ]);

    readonly dialogTitle = computed(() =>
        this.translocoService.translate('title_block_entity', {
            entityType: this.entityTypeLabel(),
        })
    );

    constructor() {
        effect(() => {
            if (!this.visible()) {
                return;
            }

            this.formData.set({
                entityType: this.entityType(),
                entityName: this.entityName(),
                reason: null,
                description: '',
            });
            queueMicrotask(() => this.validateForm());
        });
    }

    onFormContentReady(e: DxFormTypes.ContentReadyEvent): void {
        const formInstance = e.component?.instance();
        if (formInstance && typeof formInstance.validate === 'function') {
            formInstance.validate();
        }
    }

    onFormFieldDataChanged() {
        this.isValid.set(!!this.formData().reason);
    }

    onVisibleChange(visible: boolean): void {
        this.visibleChange.emit(visible);
        if (!visible) {
            this.resetForm();
        }
    }

    onFooterClicked(action: string): void {
        if (action === 'close') {
            this.onVisibleChange(false);
            return;
        }

        if (action === 'block') {
            this.emitBlockConfirmed();
        }
    }

    private emitBlockConfirmed(): void {
        const entityId = this.entityId();
        const { reason, description } = this.formData();

        if (!entityId || !reason) {
            return;
        }

        this.blockConfirmed.emit({
            entityType: this.entityType(),
            entityId,
            entityName: this.entityName(),
            reason,
            description: description?.trim() || null,
        });
    }

    private resetForm(): void {
        this.formData.set({
            entityType: this.entityType(),
            entityName: this.entityName(),
            reason: null,
            description: '',
        });
    }

    private validateForm(): void {
        this.form?.instance?.validate();
    }
}
